import { useState } from "react";

import { Button, Callout } from "@blueprintjs/core";

import { pluralize } from "@/components/helpers/string-utils";
import { Box } from "@/components/layout/flexbox";
import {
  UserGroupRow,
  UserListStyled,
} from "@/components/pages_logged_in/workspace-admin/SetGroupForUsersDialog";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { useDeactivateWorkspaceMembersMutation } from "@/graphql";
import { StatsMember } from "@/types/workspaces";

interface ResultsProps {
  result: { success?: boolean };
  onClose: () => void;
  members: StatsMember[];
}

function Results({ result, onClose, members }: ResultsProps) {
  const resultView =
    result && result.success ? (
      <Callout intent="success" icon="tick">
        {pluralize({
          amount: members.length,
          singular: "User",
          plural: "Users",
          showCount: false,
        })}{" "}
        successfully removed
      </Callout>
    ) : (
      <Callout intent="warning">
        We could not process your request at this time. Please contact us.
      </Callout>
    );
  return (
    <Box>
      {resultView}
      <Box mt={16}>
        <Button
          intent="primary"
          large={true}
          onClick={() => onClose()}
          loading={false}
        >
          Close
        </Button>
      </Box>
    </Box>
  );
}

interface RemoveUsersDialogProps {
  members: StatsMember[];
  selectAllUsers?: boolean;
  excludedUsers?: string[];
  totalRecords?: number;
  open: boolean;
  onClose: () => void;
  afterDeletedMember: () => void;
}

export function RemoveUsersDialog({
  members,
  selectAllUsers = false,
  excludedUsers = [],
  totalRecords = 0,
  open,
  onClose,
  afterDeletedMember,
}: RemoveUsersDialogProps) {
  const [result, setResult] = useState<{ success?: boolean }>();

  // Calculate the actual number of users that will be affected
  const affectedUsersCount = selectAllUsers
    ? totalRecords - excludedUsers.length
    : members.length;

  const [deactivateMembers, { loading }] =
    useDeactivateWorkspaceMembersMutation({
      onCompleted: (data) => {
        const { ok } = data?.deactivateWorkspaceMembers || {};
        setResult({ success: ok ?? false });
        if (ok) afterDeletedMember();
      },
      onError: () => {
        setResult({ success: false });
      },
    });

  return (
    <StyledDialog
      isOpen={open}
      lazy={true}
      title="Remove Users"
      width="480px"
      onClose={() => {
        onClose();
      }}
      onOpening={() => {
        setResult(undefined);
      }}
    >
      <Box py={3} px={4}>
        {result ? (
          <Results result={result} onClose={onClose} members={members} />
        ) : (
          <Box>
            <Box css={{ padding: "10px 0px" }}>
              {members.length === 0 ? (
                <Box>
                  You cannot remove your own account. To close your workspace,
                  please contact us at{" "}
                  <a href={`mailto:hello@exec.com`}>hello@exec.com</a>
                </Box>
              ) : (
                <Box>
                  <p>Removing these users will:</p>
                  <ul
                    style={{ margin: "8px 0", paddingLeft: "20px" }}
                    className="list-disc"
                  >
                    <li>
                      Return their personal credits to the workspace account
                    </li>
                    <li>
                      Return any group-allocated credits to their original group
                      accounts
                    </li>
                    <li>Remove them from all workspace groups</li>
                    <li>Remove them from all active programs</li>
                    <li>Revoke their access to the workspace</li>
                  </ul>
                  <p>
                    Their historical data will be preserved for analytics
                    purposes.
                  </p>
                </Box>
              )}
            </Box>
            {members.length > 0 && (
              <UserListStyled>
                {members.map((member) => {
                  return (
                    <UserGroupRow
                      key={`member-remove-${member.id}`}
                      member={member}
                      isShowGroup={false}
                      isShowCredits={true}
                    />
                  );
                })}
              </UserListStyled>
            )}
            <Box mt={4}>
              <Button
                intent="danger"
                large={true}
                disabled={loading || members.length === 0}
                onClick={() =>
                  deactivateMembers({
                    variables: {
                      workspaceMemberIds: members.map((item) => item.id),
                    },
                  })
                }
                loading={loading}
              >
                Remove{" "}
                {pluralize({
                  amount: affectedUsersCount,
                  singular: "User",
                  plural: "Users",
                  showCount: false,
                })}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </StyledDialog>
  );
}
